import { useCallback, useEffect, useRef, useState } from "react";
import { errorText } from "../../api/client";
import { useStore } from "../store";
import { kidStyle } from "../kid-style";
import { tapFeedback, starFeedback } from "../feedback";
import { markStarCelebrated } from "../last-star";
import { reviewLine, signed } from "../format";
import { HoldButton } from "../components/HoldButton";
import { ShootingStar } from "../components/ShootingStar";
import { StarIcon } from "../components/StarIcon";
import { TapGroup } from "../components/TapGroup";
import { KidAvatar } from "../components/KidAvatar";
import { canSeeTaps } from "../../shared/roles";
import { START_SCORE } from "../../shared/score";
import { closeOutdated } from "../../shared/stars";
import { addDays, dayKey, timeOfDay } from "../../shared/time";
import type { DayRecord, PointEvent } from "../../shared/types";
import { Emoji } from "../components/Emoji";

type Which = "today" | "yesterday";

/**
 * The evening review: a grown-up and the kid go through the day's taps together, then the
 * grown-up holds to close it. Closing at or above the goal earns the star there and then, and
 * today can be closed again until midnight, when a day nobody closed closes by itself. The
 * Yesterday tab only shows how yesterday ended.
 */
export function Review({ kidId, onBack }: { kidId: string; onBack: () => void }) {
  const { snapshot, loadDayEvents, loadDays, loadHistory, closeDay } = useStore();
  const kid = snapshot?.kids.find((k) => k.id === kidId);
  const isAdult = snapshot?.me.role === "adult";
  // A sibling sees how the day went, but not the taps behind it.
  const canSee = snapshot ? canSeeTaps(snapshot.me, kidId) : false;
  const today = dayKey(Date.now());
  const [which, setWhich] = useState<Which>("today");
  const day = which === "today" ? today : addDays(today, -1);

  const [events, setEvents] = useState<PointEvent[] | null>(null);
  const [opening, setOpening] = useState(START_SCORE);
  const [endScore, setEndScore] = useState(START_SCORE);
  const [record, setRecord] = useState<DayRecord | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Only the newest load may write, so switching days quickly cannot show the wrong day.
  const loads = useRef(0);

  const load = useCallback(async () => {
    const mine = ++loads.current;
    try {
      const [taps, closed] = await Promise.all([
        canSee
          ? loadDayEvents(kidId, day).then((t) => ({ ...t, end: t.events.at(-1)?.scoreAfter ?? t.opening }))
          : loadHistory(kidId, day, day).then((h) => ({
              events: [],
              opening: h.days[0]?.start ?? h.opening,
              end: h.days[0]?.end ?? h.opening,
            })),
        loadDays(kidId, day, day),
      ]);
      if (mine !== loads.current) return;
      setEvents(taps.events);
      setOpening(taps.opening);
      setEndScore(taps.end);
      setRecord(closed[0] ?? null);
    } catch (e) {
      if (mine === loads.current) setErr(errorText(e, "Couldn't load the day."));
    }
  }, [kidId, day, canSee, loadDayEvents, loadDays, loadHistory]);

  // Re-read whenever the snapshot moves (coming back to the app, or closing the day here), so a
  // day the other grown-up closed on their phone shows as closed on this one too.
  useEffect(() => {
    void load();
  }, [load, snapshot]);

  // The kid was removed (on this phone or another): nothing to show.
  useEffect(() => {
    if (snapshot && !kid) onBack();
  }, [snapshot, kid, onBack]);

  if (!kid) return null;

  const canGoBack = addDays(today, -1) >= dayKey(kid.createdAt);

  function choose(next: Which) {
    if (next === which) return;
    tapFeedback();
    setEvents(null);
    setRecord(null);
    setErr(null);
    setWhich(next);
  }

  async function close() {
    if (busy) return;
    tapFeedback(25);
    setBusy(true);
    setErr(null);
    try {
      const closed = await closeDay(kidId, day);
      setRecord(closed);
      // Only a close that won the star celebrates, not re-closing a day that already had one.
      if (closed.fresh && closed.star) {
        starFeedback();
        // Celebrated here, so Rewards on this phone does not play it a second time.
        markStarCelebrated(kidId, day);
        setCelebrate(true);
      }
    } catch (e) {
      setErr(errorText(e, "Couldn't close the day."));
    } finally {
      setBusy(false);
    }
  }

  // The headline is always the score now (today's live one from the snapshot, which the
  // 30-second refresh keeps current); the close, if any, is its own line underneath.
  const headline =
    which === "today"
      ? reviewLine(kid.score, kid.goal, false)
      : reviewLine(endScore, record?.goal ?? kid.goal, true);
  const dayEnd = which === "today" ? kid.score : endScore;
  const good = (events ?? []).filter((e) => e.delta > 0);
  const notSoGood = (events ?? []).filter((e) => e.delta < 0);
  const dayWord = which === "today" ? "today" : "yesterday";
  // Only today can be closed, and closed again until midnight; past days closed by themselves.
  const canClose = isAdult && which === "today";
  const closeControl = (
    <section className="review-close">
      <HoldButton
        className="close-hold"
        label={`Hold to close today for ${kid.name}`}
        onHoldStart={() => tapFeedback()}
        onConfirm={() => void close()}
      >
        {busy ? "Closing…" : record ? "Update today's close" : "Close today together"}
      </HoldButton>
      <p className="hint">{`Press and hold. ${kid.goal} or more earns a star.`}</p>
    </section>
  );

  return (
    <main className="screen" style={kidStyle(kid.colour)}>
      <button className="back" type="button" onClick={onBack}>
        <span aria-hidden="true">‹</span> Back
      </button>

      {canGoBack && (
        <div className="segmented" role="tablist" aria-label="Which day">
          <button
            type="button"
            role="tab"
            aria-selected={which === "today"}
            className="segmented__option"
            onClick={() => choose("today")}
          >
            Today
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={which === "yesterday"}
            className="segmented__option"
            onClick={() => choose("yesterday")}
          >
            Yesterday
          </button>
        </div>
      )}

      <section className="review-head">
        <KidAvatar kid={kid} size="lg" />
        <h1 className="review-head__name">{kid.name}</h1>
        <p className="review-head__line">{events || record ? headline : "…"}</p>
        {events && <p className="review-head__net">{`Net ${dayWord} ${signed(dayEnd - opening)}`}</p>}
      </section>

      {err && <p className="error">{err}</p>}

      {events && canSee && (
        <>
          <TapGroup title="Good" emoji="👍" events={good} empty="Nothing here yet." />
          <TapGroup title="Not so good" emoji="🙈" events={notSoGood} empty="Nothing here. Great day!" />
        </>
      )}
      {events && !canSee && <p className="hint">{`Only grown-ups and ${kid.name} can see ${kid.name}'s taps.`}</p>}

      {record ? (
        <section className="review-closed">
          <p className="review-closed__result">
            {record.star ? (
              <>
                <StarIcon className="review-closed__star" /> Star day
              </>
            ) : (
              "No star this time"
            )}
          </p>
          <p className="hint">
            <Emoji>✅</Emoji>
            {closedLine(record)}
          </p>
          {which === "today" && closeOutdated(kid.today, kid.score, kid.goal) && (
            <p className="hint close-moved">
              {isAdult ? "Score moved since the close · Update today's close" : "Score moved since the close"}
            </p>
          )}
        </section>
      ) : (
        events &&
        which === "today" &&
        !isAdult && <p className="hint">A grown-up closes the day with you.</p>
      )}

      {events && canClose && closeControl}

      {celebrate && <ShootingStar onDone={() => setCelebrate(false)} />}
    </main>
  );
}

/** "Closed at 20:21 by anthony at 96 · star", or "Closed by itself at midnight at 84 · no star". */
function closedLine(record: DayRecord): string {
  const result = `${record.closingScore} · ${record.star ? "star" : "no star"}`;
  return record.closedBy === "auto"
    ? `Closed by itself at midnight at ${result}`
    : `Closed at ${timeOfDay(record.closedAt)} by ${record.closedBy} at ${result}`;
}
